import { useQuery } from "@apollo/client";
import { Alert, Box, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useCallback } from "react";

import {
  CreateSuppressionsFromShortcodeSubscribersMutation,
  CreateSuppressionsFromShortcodeSubscribersMutationVariables,
  GetSubaccountShortcodeQuery,
  GetSubaccountShortcodeQueryVariables,
  GetSubscriberSummaryQuery,
  GetSubscriberSummaryQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import TooltipButton from "~frontend/components/TooltipButton";
import { CREATE_SUPPRESSIONS_FROM_SHORTCODE_SUBSCRIBERS } from "~frontend/graphql/campaigns";
import {
  GET_SUBACCOUNT_SHORTCODE,
  GET_SUBSCRIBER_SUMMARY,
} from "~frontend/graphql/shortcode";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import { isSendInProgress } from "./sendInProgress";
import { isSuppressionJobRunning } from "./suppressionJobs";

export function SuppressShortcodeSubscribers({
  campaign,
}: {
  campaign: CampaignWithSuppressionData;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const errorMessage = useErrorMessageFormatter();
  const confirm = useConfirm();

  const shortcodeQuery = useQuery<
    GetSubaccountShortcodeQuery,
    GetSubaccountShortcodeQueryVariables
  >(GET_SUBACCOUNT_SHORTCODE, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const shortcodeId = shortcodeQuery.data?.getSubaccount.shortcode?.id;

  const summaryQuery = useQuery<
    GetSubscriberSummaryQuery,
    GetSubscriberSummaryQueryVariables
  >(GET_SUBSCRIBER_SUMMARY, {
    variables: {
      input: {
        shortcodeId: shortcodeId ?? "",
      },
    },
    skip: !shortcodeId,
    fetchPolicy: "network-only",
  });

  const [startSuppressionJob, { loading: startingSuppressionJob }] =
    useMutation<
      CreateSuppressionsFromShortcodeSubscribersMutation,
      CreateSuppressionsFromShortcodeSubscribersMutationVariables
    >(CREATE_SUPPRESSIONS_FROM_SHORTCODE_SUBSCRIBERS, {
      onErrorSnackbarMessage: "Error starting suppression",
    });

  const handleStartSuppressionJob = useCallback(async () => {
    if (!shortcodeId) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Suppress Shortcode Subscribers",
      description:
        "This will suppress all shortcode subscribers from this campaign.",
    });
    if (!confirmed) {
      return;
    }

    await startSuppressionJob({
      variables: { input: { campaignId: campaign.id, shortcodeId } },
    });
  }, [shortcodeId, confirm, startSuppressionJob, campaign]);

  const subscriberCount =
    summaryQuery.data?.getSubscriberSummary.activeSubscribers;

  if (!subscriberCount) {
    return <></>;
  }

  if (shortcodeQuery.error || summaryQuery.error) {
    return (
      <Alert severity="error">
        Error fetching subscribers:{" "}
        {errorMessage(shortcodeQuery.error ?? summaryQuery.error)}
      </Alert>
    );
  }

  const sendInProgress = isSendInProgress(campaign);

  return (
    <Box sx={{ backgroundColor: palette.lightestGray, padding: "24px 36px" }}>
      <FlexBox justifyContent="space-between" direction="row">
        <Box>
          <Typography variant="body1Bold">Shortcode Subscribers</Typography>
          <Typography variant="body1">
            {plural(subscriberCount, "Active Subscriber", "Active Subscribers")}
          </Typography>
        </Box>
        <TooltipButton
          variant="contained"
          onClick={handleStartSuppressionJob}
          disabled={
            sendInProgress ||
            startingSuppressionJob ||
            isSuppressionJobRunning(campaign)
          }
          tooltipText={
            sendInProgress
              ? "Cannot suppress contacts while sending is in progress"
              : undefined
          }
        >
          Suppress All Shortcode Subscribers
        </TooltipButton>
      </FlexBox>
    </Box>
  );
}
